import {
    useCreateCartItemsMutation,
    useDeleteCartItemsByIdMutation,
    useDeleteCartItemByIdMutation,
    useDeleteCartMutation,
    CreateCartItemCommand2,
    CreateCartItemBundleCommand,
    CartItemVoucherProductDetailDto,
} from 'store/api-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import { useCart } from 'store/cart-slice/hooks'
import { ErrorField, handleError } from 'helpers/errors'
import { PromiseResponse } from 'helpers/types'
import { setIsErrorModalOpened } from 'store/general-slice'
import { VoucherBox, removeAllVouchers } from '.'
import { VoucherType, VoucherWithDetails } from 'store/api-slice/types'
import { v4 as uuidv4 } from 'uuid'

const CART_CREATOR_TYPE = 'voucher'

export const useVouchers = (): {
    addVouchersToCart: () => Promise<PromiseResponse>
    removeVouchersFromCart: (
        isFullReset?: boolean,
        shouldSkipRefetch?: boolean
    ) => Promise<PromiseResponse>
    clearItemsFromCart: () => Promise<PromiseResponse>
    isProcessing: boolean
    removeShipping: () => Promise<void>
} => {
    const dispatch = useAppDispatch()

    const { vouchersBoxes } = useAppSelector((state) => state.vouchers)
    const {
        items: { vouchers, shipping },
    } = useAppSelector((state) => state.cart)

    const { language } = useApp()
    const { refetchCart } = useCart()

    const [postCartItems, postStatus] = useCreateCartItemsMutation()
    const [deleteCart, deleteStatus] = useDeleteCartMutation()
    const [deleteItems, deleteItemsStatus] = useDeleteCartItemsByIdMutation()
    const [deleteItem, deleteItemStatus] = useDeleteCartItemByIdMutation()

    const isProcessing =
        postStatus.isLoading ||
        deleteStatus.isLoading ||
        deleteItemsStatus.isLoading ||
        deleteItemStatus.isLoading

    // TODO: get rid off every 'x as type'

    const addVouchersToCart = async () => {
        const valueVouchersToSend: CreateCartItemCommand2[] = vouchersBoxes
            .filter(
                (voucher) => voucher.voucherType === ('amount' as VoucherType)
            )
            .map(
                ({
                    cartItemId,
                    productId,
                    voucherType,
                    voucherSignature,
                    amount,
                    productType,
                }: VoucherBox) => {
                    const emptyCartItem = {
                        cartItemId: '',
                        productType:
                            'voucher' as CreateCartItemCommand2['productType'],
                        productId: '',
                        quantity: 0,
                    }

                    if (
                        !cartItemId ||
                        !productId ||
                        !voucherType ||
                        !voucherSignature ||
                        !productType ||
                        !amount
                    )
                        return { ...emptyCartItem }

                    return {
                        cartItemId,
                        productType:
                            productType as CreateCartItemCommand2['productType'],
                        productId,
                        quantity: 1,
                        detail: {
                            voucherType: 'amount',
                            voucherSignature,
                            productType,
                            amount: amount || 0,
                        },
                    }
                }
            )
        const cardVouchersToSend: CreateCartItemBundleCommand[] = vouchersBoxes
            .filter((voucher) => voucher.voucherType === 'product')
            .map(
                ({
                    productId,
                    voucherType,
                    voucherSignature,
                    productType,
                    bundleData,
                    cartItemId,
                }: VoucherBox) => {
                    const emptyCartItem = {
                        cartItemBundleId: '',
                        productId: '',
                        quantity: 0,
                    }
                    const cartItemBundleId = uuidv4()

                    if (
                        !cartItemBundleId ||
                        !cartItemId ||
                        !productId ||
                        !voucherType ||
                        !voucherSignature ||
                        !productType ||
                        !bundleData
                    )
                        return { ...emptyCartItem }

                    const bundleItemsToSend = bundleData.map(
                        ({
                            productId: itemProductId,
                            productType: itemProductType,
                        }) => {
                            const isCardItem = itemProductType === 'card'
                            const commonItemData = {
                                productId: itemProductId,
                                quantity: 1,
                                cartItemBundleId,
                                cartCreatorType: CART_CREATOR_TYPE,
                                productType:
                                    productType as CreateCartItemCommand2['productType'],
                            }

                            return isCardItem
                                ? {
                                      ...commonItemData,
                                      cartItemId,
                                      detail: {
                                          voucherType:
                                              voucherType as CartItemVoucherProductDetailDto['voucherType'],
                                          productType:
                                              productType as CreateCartItemCommand2['productType'],
                                          voucherSignature: [
                                              ...voucherSignature,
                                          ],
                                      },
                                  }
                                : {
                                      ...commonItemData,
                                      cartItemId: uuidv4(),
                                  }
                        }
                    )

                    return {
                        cartItemBundleId,
                        productId,
                        quantity: 1,
                        items: [...bundleItemsToSend],
                    }
                }
            )

        try {
            handleError(
                await postCartItems({
                    createCartItemsCommand: {
                        items: valueVouchersToSend,
                        itemsBundle: cardVouchersToSend,
                    },
                    cartCreatorType: CART_CREATOR_TYPE,
                    _locale: language,
                })
            )
            refetchCart(true)
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }

            if (!messages) dispatch(setIsErrorModalOpened(true))
            return { isSuccess: false, messages }
        }
    }

    const removeVouchersFromCart = async (
        isFullReset = false,
        shouldSkipRefetch = false
    ) => {
        try {
            handleError(
                await deleteCart({
                    _locale: language,
                    cartCreatorType: CART_CREATOR_TYPE,
                })
            )
            if (isFullReset) dispatch(removeAllVouchers())
            if (!shouldSkipRefetch) refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    const clearItemsFromCart = async () => {
        const shippingId = shipping?.cartItemId
        const itemsToDelete: string[] = []

        if (isProcessing) return { isSuccess: true }

        try {
            shippingId && itemsToDelete.push(shippingId)
            vouchers.forEach((voucher: VoucherWithDetails) => {
                if (voucher.itemsBundle && voucher.itemsBundle.length) {
                    voucher.itemsBundle.forEach(({ cartItemId }) => {
                        cartItemId && itemsToDelete.push(cartItemId)
                    })
                } else
                    voucher.cartItemId && itemsToDelete.push(voucher.cartItemId)
            })

            if (itemsToDelete.length) {
                handleError(
                    await deleteItems({
                        _locale: language,
                        cartCreatorType: CART_CREATOR_TYPE,
                        deleteCartItemsByIdCommand: {
                            cartItemUuids: itemsToDelete,
                        },
                    })
                )
                refetchCart()
            }

            return { isSuccess: true }
        } catch (errors: unknown) {
            const { messages } = errors as {
                messages: ErrorField[]
            }
            return { isSuccess: false, messages }
        }
    }

    // TODO: try to do this globally in cart or order slice
    const removeShipping = async () => {
        const shippingId = shipping?.cartItemId

        if (!shippingId) return

        try {
            handleError(
                await deleteItem({
                    _locale: language,
                    cartItemId: shippingId,
                    cartCreatorType: CART_CREATOR_TYPE,
                })
            )
            refetchCart()
        } catch (error) {
            dispatch(setIsErrorModalOpened(true))
        }
    }

    return {
        addVouchersToCart,
        removeVouchersFromCart,
        clearItemsFromCart,
        removeShipping,
        isProcessing,
    }
}
